import React, { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TopTabbar from '../components/partials/TopTabbar';
import Header from '../components/partials/Header';
import Footer from '../components/partials/Footer';
import CreateUserModal from '../components/modals/user/CreateUserModal';
import UserDetailModal from '../components/modals/user/UserDetailModal';
import UserService from '../services/UserService';
import LoadingSpinner from '../utils/LoadingSpinner';

const PAGE_SIZE_DEFAULT = 10;

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterIdDocsVerified, setFilterIdDocsVerified] = useState(null);
    const [filterIdentificationRejected, setFilterIdentificationRejected] = useState(false);
    const [filterIdentificationResubmitted, setFilterIdentificationResubmitted] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);

    // Pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        fetchUsers(currentPage, pageSize);
    }, [currentPage, pageSize]);

    /**
     * Récupère les utilisateurs depuis le backend (Page Spring).
     * Le service retourne la réponse brute :
     * { content: [], totalElements, totalPages, number, size, ... }
     */
    const fetchUsers = async (page = 0, size = pageSize) => {
        try {
            setIsLoadingMore(true);

            const data = await UserService.getAllUsers(page, size, 'fr');

            // Extraction de l'objet Page Spring
            setUsers(data.content ?? []);
            setTotalPages(data.totalPages ?? 0);
            setTotalElements(data.totalElements ?? 0);
            setCurrentPage(data.number ?? 0);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Erreur lors de la récupération des utilisateurs');
        } finally {
            setIsPageLoading(false);
            setIsLoadingMore(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(0); // Retour page 1 lors du changement de taille
    };

    // ─── CRUD ──────────────────────────────────────────────────────────────────

    const handleCreateUser = async (newUser) => {
        try {
            await UserService.registerUser(newUser);
            setShowCreateModal(false);
            toast.success('Utilisateur créé avec succès');
            // Retour à la première page après création
            if (currentPage !== 0) {
                setCurrentPage(0);
            } else {
                fetchUsers(0, pageSize);
            }
        } catch (error) {
            console.error('Error creating user:', error);
            toast.error("Erreur lors de la création de l'utilisateur");
        }
    };

    const handleUpdateUser = async (updatedUser) => {
        try {
            await UserService.updateUser(updatedUser);
            fetchUsers(currentPage, pageSize);
            setShowDetailModal(false);
            toast.success('Utilisateur mis à jour avec succès');
        } catch (error) {
            console.error('Error updating user:', error);
            toast.error("Erreur lors de la mise à jour de l'utilisateur");
        }
    };

    const handleDeleteUser = async (user, e) => {
        e.stopPropagation();
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: `Voulez-vous vraiment supprimer l'utilisateur ${user.firstName} ${user.lastName} ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await UserService.deleteUser(user.id);
                    toast.success(
                        `L'utilisateur ${user.firstName} ${user.lastName} a été supprimé avec succès`
                    );
                    // Si la page courante n'a plus qu'un seul élément, reculer d'une page
                    if (users.length === 1 && currentPage > 0) {
                        setCurrentPage(currentPage - 1);
                    } else {
                        fetchUsers(currentPage, pageSize);
                    }
                } catch (error) {
                    console.error('Error deleting user:', error);
                    toast.error("Erreur lors de la suppression de l'utilisateur");
                }
            }
        });
    };

    const openUserDetail = (user) => {
        setSelectedUser(user);
        setShowDetailModal(true);
    };

    const resetFilters = () => {
        setFilterRole('');
        setFilterIdDocsVerified(null);
        setFilterIdentificationRejected(false);
        setFilterIdentificationResubmitted(false);
    };

    // ─── Filtrage côté client (sur la page courante) ───────────────────────────

    const filteredUsers = users.filter((user) => {
        const matchesSearch =
            user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = filterRole ? user.roleNames?.includes(filterRole) : true;
        const matchesIdDocsVerified =
            filterIdDocsVerified !== null ? user.idDocsVerified === filterIdDocsVerified : true;
        const matchesRejected = filterIdentificationRejected
            ? user.identificationOutcome === 'REJECTED'
            : true;
        const matchesResubmitted = filterIdentificationResubmitted
            ? user.identificationOutcome === 'RESUBMITTED'
            : true;

        return matchesSearch && matchesRole && matchesIdDocsVerified && matchesRejected && matchesResubmitted;
    });

    const isNoFilter =
        !filterRole &&
        filterIdDocsVerified === null &&
        !filterIdentificationRejected &&
        !filterIdentificationResubmitted;

    // ─── Statut d'identification ───────────────────────────────────────────────

    const renderIdentificationStatus = (user) => {
        if (!user.idDocsVerified && !user.identificationOutcome) {
            return <small style={{ color: '#3b82f6' }}>Identification non soumise</small>;
        }
        if (
            !user.idDocsVerified &&
            user.identificationOutcome &&
            user.identificationOutcome !== 'REJECTED' &&
            user.identificationOutcome !== 'RESUBMITTED'
        ) {
            return <small style={{ color: '#c87f12' }}>Identification soumise</small>;
        }
        if (user.identificationOutcome === 'REJECTED') {
            return <small style={{ color: 'red' }}>Identification rejetée</small>;
        }
        if (user.identificationOutcome === 'RESUBMITTED') {
            return <small style={{ color: '#fba503' }}>Identification resoumise</small>;
        }
        return null;
    };

    // ─── Rendu ─────────────────────────────────────────────────────────────────

    if (isPageLoading) {
        return <LoadingSpinner isVisible={true} loadingText="Chargement des utilisateurs..." />;
    }

    return (
        <>
            <div className="d-flex flex-column vh-100">
                <div className="flex-grow-1 overflow-y-scroll">
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', maxWidth: '800px', margin: '0 auto' }}>
                        <div className="verify-number-main">
                            <TopTabbar />
                            <Header />

                            {/* ── En-tête ── */}
                            <div className="verify-number-top">
                                <div className="container">
                                    <div className="verify-number-top-content d-flex justify-content-between align-items-center">
                                        <div className="header-title mt-32">
                                            <p style={{ fontSize: '25px' }}>Gestion des utilisateurs</p>
                                        </div>
                                        <button
                                            className="btn btn-success mt-32"
                                            onClick={() => setShowCreateModal(true)}
                                        >
                                            <i className="fas fa-plus me-2"></i> Ajouter un utilisateur
                                        </button>
                                    </div>

                                    {/* Barre de recherche */}
                                    <div className="contact-search">
                                        <div className="input-group contact-searchbar">
                                            <div className="search-icon">
                                                <img src="assets/svg/search-icon.svg" alt="search-icon" />
                                            </div>
                                            <div className="seach-bar">
                                                <input
                                                    type="search"
                                                    placeholder="Rechercher un nom, ou un email"
                                                    className="form-control search-text"
                                                    id="search-input"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── Corps ── */}
                            <div className="verify-number-bottom" id="send-money-contact">
                                {/* Onglets de filtre */}
                                <div className="container mb-35" style={{ marginTop: '-40px' }}>
                                    <div className="nav nav-tabs custom-tab-contact" id="nav-tab" role="tablist">
                                        <button
                                            className={`nav-link ${isNoFilter ? 'active' : ''}`}
                                            type="button"
                                            onClick={() => {
                                                resetFilters();
                                                setSearchTerm('');
                                            }}
                                        >
                                            Tous les utilisateurs
                                        </button>

                                        <Dropdown className="text-center" style={{ marginLeft: '15%' }}>
                                            <Dropdown.Toggle variant="link" className="nav-link dropdown-toggle">
                                                <i className="fas fa-filter me-2"></i>Filtrer
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={resetFilters}>Tous les rôles</Dropdown.Item>
                                                <Dropdown.Item onClick={() => { resetFilters(); setFilterRole('ADMIN'); }}>
                                                    Administrateur
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => { resetFilters(); setFilterRole('USER'); }}>
                                                    Utilisateur
                                                </Dropdown.Item>
                                                <Dropdown.Divider />
                                                <Dropdown.Item onClick={() => { resetFilters(); setFilterIdDocsVerified(true); }}>
                                                    Identifiés
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => { resetFilters(); setFilterIdDocsVerified(false); }}>
                                                    Non identifiés
                                                </Dropdown.Item>
                                                <Dropdown.Divider />
                                                <Dropdown.Item onClick={() => { resetFilters(); setFilterIdentificationRejected(true); }}>
                                                    Identification rejetée
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => { resetFilters(); setFilterIdentificationResubmitted(true); }}>
                                                    Identification resoumise
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                </div>

                                {/* ── Liste ── */}
                                <div className="verify-number-bottom-wrap" style={{ marginTop: '77px' }}>
                                    {isLoadingMore ? (
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <LoadingSpinner isVisible={true} loadingText="Chargement des utilisateurs..." />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="send-money-contact">
                                                {filteredUsers.length === 0 ? (
                                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                                        <img
                                                            src="assets/images/setting/no_data.png"
                                                            alt="Aucune donnée"
                                                            style={{ width: '100px', marginBottom: '20px' }}
                                                        />
                                                        <p style={{ color: '#666', fontSize: '1.2em' }}>
                                                            Aucun utilisateur trouvé
                                                        </p>
                                                    </div>
                                                ) : (
                                                    filteredUsers.map((user) => (
                                                        <div
                                                            key={user.id}
                                                            className="send-money-contact-tab request-money1 p-1 mb-2"
                                                            onClick={() => openUserDetail(user)}
                                                            style={{ borderBottom: '3px solid #5a5c5e', cursor: 'pointer' }}
                                                        >
                                                            {/* Avatar */}
                                                            <div className="contact-profile">
                                                                <img
                                                                    style={{ height: '80px', width: '80px' }}
                                                                    src={user.profileImageUrl || 'assets/images/invite-friend/avatar.jpg'}
                                                                    alt={`${user.firstName} ${user.lastName}`}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = 'assets/images/invite-friend/avatar.jpg';
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* Infos */}
                                                            <div className="contact-details" style={{ flex: 1, minWidth: 0 }}>
                                                                <h3 style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {user.firstName} {user.lastName}
                                                                </h3>
                                                                <h4 style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {user.email}
                                                                </h4>
                                                                <small className="text-muted">
                                                                    {Array.isArray(user.roleNames)
                                                                        ? user.roleNames.join(', ')
                                                                        : user.roleNames || 'N/A'}
                                                                </small>
                                                                <br />
                                                                <small className={`text-${user.idDocsVerified ? 'success' : 'danger'}`}>
                                                                    {user.idDocsVerified ? 'Identifié' : 'Non identifié'}
                                                                </small>
                                                                <br />
                                                                {renderIdentificationStatus(user)}
                                                            </div>

                                                            {/* Suppression */}
                                                            <div className="contact-star">
                                                                <div className="star-favourite">
                                                                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                                                    <a
                                                                        href="#"
                                                                        onClick={(e) => handleDeleteUser(user, e)}
                                                                    >
                                                                        <i
                                                                            className="fas fa-trash-alt me-2"
                                                                            style={{ color: 'red', fontSize: '20px' }}
                                                                        ></i>
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            {/* ── Pagination ── */}
                                            {totalElements > 0 && (
                                                <div
                                                    className="pagination-controls"
                                                    style={{
                                                        padding: '20px',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        flexWrap: 'wrap',
                                                        gap: '10px',
                                                        marginTop: '20px',
                                                    }}
                                                >
                                                    {/* Compteur */}
                                                    <div className="pagination-info">
                                                        <small className="text-muted">
                                                            Affichage de {users.length} sur {totalElements} utilisateurs
                                                        </small>
                                                    </div>

                                                    {/* Boutons de navigation */}
                                                    <div
                                                        className="pagination-buttons"
                                                        style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
                                                    >
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handlePageChange(0)}
                                                            disabled={currentPage === 0}
                                                            title="Première page"
                                                        >
                                                            <i className="fas fa-angle-double-left"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handlePageChange(currentPage - 1)}
                                                            disabled={currentPage === 0}
                                                            title="Page précédente"
                                                        >
                                                            <i className="fas fa-angle-left"></i>
                                                        </button>

                                                        <span className="mx-2">
                                                            Page {currentPage + 1} / {totalPages}
                                                        </span>

                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handlePageChange(currentPage + 1)}
                                                            disabled={currentPage >= totalPages - 1}
                                                            title="Page suivante"
                                                        >
                                                            <i className="fas fa-angle-right"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handlePageChange(totalPages - 1)}
                                                            disabled={currentPage >= totalPages - 1}
                                                            title="Dernière page"
                                                        >
                                                            <i className="fas fa-angle-double-right"></i>
                                                        </button>
                                                    </div>

                                                    {/* Taille de page */}
                                                    <div className="page-size-selector">
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={pageSize}
                                                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                                            style={{ width: 'auto' }}
                                                        >
                                                            {[5, 10, 25, 50].map((n) => (
                                                                <option key={n} value={n}>{n} par page</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* ── Modals ── */}
                            <CreateUserModal
                                isOpen={showCreateModal}
                                onClose={() => setShowCreateModal(false)}
                                onUserCreated={handleCreateUser}
                            />
                            <UserDetailModal
                                show={showDetailModal}
                                user={selectedUser}
                                onHide={() => setShowDetailModal(false)}
                                onUpdateUser={handleUpdateUser}
                                onRefreshUsers={() => fetchUsers(currentPage, pageSize)}
                            />
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UsersPage;